import type { AutomationRunDto, AutomationRunStatus } from '@tms/shared';
import { Link, useNavigate } from '@tanstack/react-router';
import { Download, ExternalLink, Play, RotateCcw } from 'lucide-react';
import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
  Button,
  buttonClassName,
  Card,
  CardHeader,
  ErrorState,
  ProgressBar,
  SkeletonRows,
  Stat,
} from '../../../components/ui';
import { useFormat, useT } from '../../../i18n';
import { cn } from '../../../lib/cn';
import {
  runReportHref,
  useAutomationCan,
  useProjectRuns,
  useRerunFailed,
  useRunCauses,
} from '../hooks';
import { AutomationStatusBadge, FailureCauses, RunHistoryTable } from '../ui';
import { downloadRunsCsv } from './export-runs';
import { Segmented, useFailToast } from './shared';

type Filter = AutomationRunStatus | 'all';
const FILTERS: Filter[] = ['all', 'running', 'success', 'failed', 'cancelled'];

const runLink = (run: AutomationRunDto) =>
  ({
    to: '/automation/runs/$runId',
    params: { runId: run.pipelineId },
    search: { project: run.projectId },
  }) as const;

const ResultFigure = ({
  label,
  value,
  className,
}: {
  label: string;
  value: string | number;
  className?: string;
}) => (
  <div className="flex flex-col gap-0.5">
    <span className={cn('text-[22px] font-bold leading-none text-ink', className)}>{value}</span>
    <span className="text-[12px] uppercase tracking-wide text-ink-subtle">{label}</span>
  </div>
);

/** The latest run: its totals, or its progress, or why it has none. */
const LastRun = ({ run }: { run: AutomationRunDto }) => {
  const { t } = useT();
  const navigate = useNavigate();
  const can = useAutomationCan();
  const failToast = useFailToast();
  const rerun = useRerunFailed();

  // a pipeline GitLab started by itself has no configuration to rerun — as on the run page
  const canRerun = can.run && run.status === 'failed' && run.trigger === 'admin';

  return (
    <Card>
      <CardHeader
        title={
          <span className="flex flex-wrap items-center gap-2">
            {t('automation.project.dashboard.lastRun', { id: run.id })}
            <AutomationStatusBadge status={run.status} />
          </span>
        }
        action={
          <div className="flex flex-wrap gap-2">
            {canRerun ? (
              <Button
                size="sm"
                loading={rerun.isPending}
                onClick={() =>
                  rerun.mutate(
                    { runId: run.id },
                    {
                      onSuccess: (next) => {
                        toast.success(t('automation.project.dashboard.rerunStarted'));
                        void navigate(runLink(next));
                      },
                      onError: failToast,
                    },
                  )
                }
              >
                <RotateCcw className="size-3.5" />
                {t('automation.project.dashboard.rerunFailed')}
              </Button>
            ) : null}
            <Link {...runLink(run)} className={buttonClassName('primary', 'sm')}>
              <Play className="size-3.5" />
              {t('automation.project.dashboard.openRun')}
            </Link>
          </div>
        }
      />
      <div className="px-4 py-3">
        {run.result ? (
          <div className="flex flex-wrap items-end gap-6">
            <ResultFigure
              label={t('automation.project.dashboard.passed')}
              value={run.result.passed}
              className="text-pass"
            />
            <ResultFigure
              label={t('automation.project.dashboard.failed')}
              value={run.result.failed}
              className="text-fail"
            />
            <ResultFigure
              label={t('automation.project.dashboard.broken')}
              value={run.result.broken}
              className="text-block"
            />
            <ResultFigure
              label={t('automation.project.dashboard.skipped')}
              value={run.result.skipped}
              className="text-skip"
            />
            <ResultFigure
              label={t('automation.project.dashboard.duration')}
              value={run.result.duration}
            />
            <a
              href={runReportHref(run.pipelineId, run.projectId)}
              download
              title={t('automation.runs.downloadReportHint')}
              className={buttonClassName('secondary', 'sm', 'ml-auto')}
            >
              <Download className="size-3.5" />
              {t('automation.runs.downloadReport')}
            </a>
            {run.allureUrl ? (
              <a
                href={run.allureUrl}
                target="_blank"
                rel="noreferrer"
                className={buttonClassName('secondary', 'sm')}
              >
                <ExternalLink className="size-3.5" />
                {t('automation.project.dashboard.allure')}
              </a>
            ) : null}
          </div>
        ) : run.status === 'running' || run.status === 'pending' ? (
          <div className="flex flex-col gap-2">
            <div className="flex items-center justify-between text-[13px]">
              <span className="text-ink-muted">{t('automation.project.dashboard.inProgress')}</span>
              <span className="font-mono">{run.progress}%</span>
            </div>
            <ProgressBar value={run.progress} />
          </div>
        ) : (
          // over without totals: no tests in GitLab's report (no test job, tests never
          // started) or the run was cancelled
          <p className="text-[13px] text-ink-muted">
            {run.status === 'cancelled'
              ? t('automation.project.dashboard.cancelledNoResult')
              : t('automation.project.dashboard.noTests')}
          </p>
        )}
      </div>
    </Card>
  );
};

/**
 * A project's runs at a glance: pass rate over the finished runs, the latest
 * run, why its tests failed, and the history with a status filter and export.
 */
export const DashboardTab = ({ projectId }: { projectId: string }) => {
  const { t, locale } = useT();
  const { relative } = useFormat();
  const runs = useProjectRuns(projectId);
  const [filter, setFilter] = useState<Filter>('all');

  const all = useMemo(() => runs.data ?? [], [runs.data]);
  const last = all[0];
  // the latest run with test totals (running ones and runs without tests have none)
  const lastDone = all.find((run) => run.result);
  // «why it failed» — for that run, when it has red tests
  const lastRed =
    lastDone?.result && lastDone.result.failed + lastDone.result.broken > 0 ? lastDone : undefined;
  const causes = useRunCauses(lastRed?.pipelineId, lastRed?.projectId);
  const shown = useMemo(
    () => all.filter((run) => filter === 'all' || run.status === filter),
    [all, filter],
  );

  // pass rate over the finished runs with totals: passed of those that ran (skipped do not count)
  const totals = useMemo(() => {
    const done = all.filter((run) => run.result);
    const passed = done.reduce((sum, run) => sum + (run.result?.passed ?? 0), 0);
    const red = done.reduce(
      (sum, run) => sum + (run.result?.failed ?? 0) + (run.result?.broken ?? 0),
      0,
    );
    return {
      passRate: passed + red ? Math.round((passed / (passed + red)) * 100) : null,
      passed,
      red,
      done: done.length,
    };
  }, [all]);

  if (runs.error && !runs.data) {
    return (
      <Card>
        <ErrorState error={runs.error} onRetry={() => void runs.refetch()} />
      </Card>
    );
  }
  if (!runs.data) return <SkeletonRows rows={4} className="p-0" />;

  const active = all.filter((run) => run.status === 'running' || run.status === 'pending').length;

  return (
    <div className="flex flex-col gap-4">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Stat
          label={t('automation.project.dashboard.passRate')}
          value={totals.passRate === null ? '—' : `${totals.passRate}%`}
          valueClassName="text-pass"
          hint={
            totals.done
              ? t('automation.project.dashboard.passRateHint', {
                  passed: totals.passed,
                  red: totals.red,
                  count: totals.done,
                })
              : t('automation.project.dashboard.noFinished')
          }
        />
        <Stat label={t('automation.project.dashboard.totalRuns')} value={all.length} />
        <Stat
          label={t('automation.project.dashboard.lastResult')}
          value={lastDone?.result ? `${lastDone.result.passed}/${lastDone.result.total}` : '—'}
          hint={lastDone ? `${lastDone.id} · ${relative(lastDone.createdAt)}` : undefined}
        />
        <Stat
          label={t('automation.project.dashboard.active')}
          value={active}
          valueClassName={active > 0 ? 'text-accent-fg' : undefined}
        />
      </div>

      {last ? <LastRun run={last} /> : null}

      {lastRed && causes.data ? (
        <FailureCauses
          causes={causes.data.causes}
          red={causes.data.red}
          subtitle={t('automation.project.dashboard.causesSubtitle', {
            id: lastRed.id,
            red: causes.data.red,
            total: causes.data.total,
          })}
          action={
            <Link {...runLink(lastRed)} className={buttonClassName('secondary', 'sm')}>
              {t('automation.project.dashboard.allTests')}
            </Link>
          }
        />
      ) : null}

      <Card>
        <CardHeader
          title={t('automation.project.dashboard.history')}
          action={
            <div className="flex flex-wrap items-center gap-2">
              <Segmented
                label={t('automation.project.dashboard.filterLabel')}
                value={filter}
                onChange={setFilter}
                options={FILTERS.map((value) => ({
                  value,
                  label:
                    value === 'all'
                      ? t('automation.project.dashboard.filterAll')
                      : t(`automation.status.${value}`),
                }))}
              />
              <Button
                size="sm"
                disabled={!shown.length}
                onClick={() => {
                  downloadRunsCsv(shown, locale);
                  toast.success(t('automation.project.dashboard.exported'));
                }}
              >
                <Download className="size-3.5" />
                {t('automation.project.dashboard.export')}
              </Button>
            </div>
          }
        />
        <RunHistoryTable
          runs={shown}
          emptyKey={
            filter === 'all'
              ? 'automation.history.empty'
              : 'automation.project.dashboard.noRunsForFilter'
          }
        />
      </Card>
    </div>
  );
};
